import { IAddress } from 'entities/compilationContext/address';
import { IBasket, IBasketCosts, IBasketItem } from 'entities/compilationContext/basket';
import {
  IDevice,
  IDeviceDevice,
  IEmail,
  IGeoLocation,
  ILocale,
  IPhone,
} from 'entities/compilationContext/compilation-context';
import { IContract } from 'entities/compilationContext/contract';
import { ICustomer, IOptIns } from 'entities/compilationContext/customer';
import { IDiscount } from 'entities/compilationContext/discount';
import { IPrice } from 'entities/compilationContext/price';
import { IRecommendations } from 'entities/compilationContext/recommendations';
import {
  IImages,
  IProduct,
  IProductCategory,
  IProductColor,
  IProductCosts,
  IProductSize,
  IProductTicket,
  IRating,
} from 'entities/compilationContext/product';
import { ISeededCompilationContext } from 'entities/compilationContext/seeded-compilation-context';
import { IShipping } from 'entities/compilationContext/shipping';
import { ITax, ITaxes } from 'entities/compilationContext/taxes';
import {
  IDuration,
  IPassengers,
  ITravelAccommodation,
  ITravelAccommodationDate,
} from 'entities/compilationContext/travel-accommodation';
import {
  IAirline,
  IGrade,
  ITravelJourney,
  ITravelWhen,
  ITravelWhere,
  ITravelWhereWhen,
} from 'entities/compilationContext/travel-journey';
import { IClientRecommendation } from 'entities/compilationContext/clientRecommendation';

export function getSampleCompilationContext(): ISeededCompilationContext {
  const defaultCompilationContext: ISeededCompilationContext = {
    unsubscribeUrl: 'unsubscribeUrl',
    emailTrackingUnsubscribeUrl: 'emailTrackingUnsubscribeUrl',
    pixelUrl: 'pixelUrl',
    year: 'year',
    abandonedBasket: getAbandonedBasket(),
    browsedProducts: [getBrowsedProducts()],
    purchasedBaskets: [],
    purchasedRecommendations: getRecommendations(),
    customer: getCustomer(),
    email: getEmail(),
    phone: getPhone(),
    geoLocation: getGeoLocation(),
    locale: getLocale(),
    device: getDevice(),
    clientRecommendations: getClientRecommendations(),
    sendOverride: 'sendOverride',
  };
  return defaultCompilationContext;
}

function getClientRecommendations(): IClientRecommendation[] {
  const clientRecommendations: IClientRecommendation[] = [
    {
      name: 'clientRecommendations.0.name',
      url: 'clientRecommendations.0.url',
      image: 'clientRecommendations.0.image',
      cost: 'clientRecommendations.0.cost',
      description: 'clientRecommendations.0.description',
      brand: 'clientRecommendations.0.brand',
      id: 'clientRecommendations.0.id',
      label: 'clientRecommendations.0.label',
      prevCost: 'clientRecommendations.0.prevCost',
      saving: 'clientRecommendations.0.saving',
      size: 'clientRecommendations.0.size',
    },
  ];
  return clientRecommendations;
}

function getAbandonedBasket_items0_product_costs_taxes(): ITaxes {
  const abandonedBasket_items0_product_costs_taxes_sales_cost: IPrice = {
    currency: 'abandonedBasket.items.0.product.costs.taxes.sales.cost.currency',
    currencySymbol: 'abandonedBasket.items.0.product.costs.taxes.sales.cost.currencySymbol',
    decimal: 'abandonedBasket.items.0.product.costs.taxes.sales.cost.decimal',
    stripped: 'abandonedBasket.items.0.product.costs.taxes.sales.cost.stripped',
    text: 'abandonedBasket.items.0.product.costs.taxes.sales.cost.text',
  };

  const abandonedBasket_items0_product_costs_taxes_sales: ITax = {
    cost: abandonedBasket_items0_product_costs_taxes_sales_cost,
    label: 'abandonedBasket.items.0.product.costs.taxes.sales.label',
  };

  const abandonedBasket_items0_product_costs_taxes: ITaxes = {
    sales: abandonedBasket_items0_product_costs_taxes_sales,
  };
  return abandonedBasket_items0_product_costs_taxes;
}

function getAbandonedBasket_items0_product_costs_previous(): IPrice {
  const abandonedBasket_items0_product_costs_previous: IPrice = {
    currency: 'abandonedBasket.items.0.product.costs.previous.currency',
    currencySymbol: 'abandonedBasket.items.0.product.costs.previous.currencySymbol',
    decimal: 'abandonedBasket.items.0.product.costs.previous.decimal',
    stripped: 'abandonedBasket.items.0.product.costs.previous.stripped',
    text: 'abandonedBasket.items.0.product.costs.previous.text',
  };
  return abandonedBasket_items0_product_costs_previous;
}

function getAbandonedBasket_items0_product_costs_singleItem(): IPrice {
  const abandonedBasket_items0_product_costs_singleItem: IPrice = {
    currency: 'abandonedBasket.items.0.product.costs.singleItem.currency',
    currencySymbol: 'abandonedBasket.items.0.product.costs.singleItem.currencySymbol',
    decimal: 'abandonedBasket.items.0.product.costs.singleItem.decimal',
    stripped: 'abandonedBasket.items.0.product.costs.singleItem.stripped',
    text: 'abandonedBasket.items.0.product.costs.singleItem.text',
  };
  return abandonedBasket_items0_product_costs_singleItem;
}

function getAbandonedBasket_discount(): IDiscount {
  const abandonedBasket_discount_cost: IPrice = {
    currency: 'abandonedBasket.discount.cost.currency',
    currencySymbol: 'abandonedBasket.discount.cost.currencySymbol',
    decimal: 'abandonedBasket.discount.cost.decimal',
    stripped: 'abandonedBasket.discount.cost.stripped',
    text: 'abandonedBasket.discount.cost.text',
  };

  const abandonedBasket_discount: IDiscount = {
    cost: abandonedBasket_discount_cost,
    label: 'abandonedBasket.discount.label',
    code: 'abandonedBasket.discount.code',
  };
  return abandonedBasket_discount;
}

function getAbandonedBasket_basketCosts_taxes(): ITaxes {
  const abandonedBasket_basketCosts_taxes_sales_cost: IPrice = {
    currency: 'abandonedBasket.basketCosts.taxes.sales.cost.currency',
    currencySymbol: 'abandonedBasket.basketCosts.taxes.sales.cost.currencySymbol',
    decimal: 'abandonedBasket.basketCosts.taxes.sales.cost.decimal',
    stripped: 'abandonedBasket.basketCosts.taxes.sales.cost.stripped',
    text: 'abandonedBasket.basketCosts taxes.sales.cost.text',
  };

  const abandonedBasket_basketCosts_taxes_sales: ITax = {
    cost: abandonedBasket_basketCosts_taxes_sales_cost,
    label: 'abandonedBasket.basketCosts.taxes.sales.label',
  };

  const abandonedBasket_basketCosts_taxes: ITaxes = {
    sales: abandonedBasket_basketCosts_taxes_sales,
  };
  return abandonedBasket_basketCosts_taxes;
}

function getAbandonedBasket_basketCosts_claim(): IPrice {
  const abandonedBasket_basketCosts_claim: IPrice = {
    currency: 'abandonedBasket.basketCosts.claim.currency',
    currencySymbol: 'abandonedBasket.basketCosts.claim.currencySymbol',
    decimal: 'abandonedBasket.basketCosts.claim.decimal',
    stripped: 'abandonedBasket.basketCosts.claim.stripped',
    text: 'abandonedBasket.basketCosts.claim.text',
  };
  return abandonedBasket_basketCosts_claim;
}

function getAbandonedBasket_basketCosts_total(): IPrice {
  const abandonedBasket_basketCosts_total: IPrice = {
    currency: 'abandonedBasket.basketCosts.total.currency',
    currencySymbol: 'abandonedBasket.basketCosts.total.currencySymbol',
    decimal: 'abandonedBasket.basketCosts.total.decimal',
    stripped: 'abandonedBasket.basketCosts.total.stripped',
    text: 'abandonedBasket.basketCosts.total.text',
  };
  return abandonedBasket_basketCosts_total;
}

function getAbandonedBasket_costs_subtotal(): IPrice {
  const abandonedBasket_costs_subtotal: IPrice = {
    currency: 'abandonedBasket.costs.subtotal.currency',
    currencySymbol: 'abandonedBasket.costs.subtotal.currencySymbol',
    decimal: 'abandonedBasket.costs.subtotal.decimal',
    stripped: 'abandonedBasket.costs.subtotal.stripped',
    text: 'abandonedBasket.costs.subtotal.text',
  };
  return abandonedBasket_costs_subtotal;
}

function getAbandonedBasket_costs(): IBasketCosts {
  const abandonedBasket_costs: IBasketCosts = {
    subtotal: getAbandonedBasket_costs_subtotal(),
    total: getAbandonedBasket_basketCosts_total(),
    taxes: getAbandonedBasket_basketCosts_taxes(),
    claim: getAbandonedBasket_basketCosts_claim(),
  };
  return abandonedBasket_costs;
}

function getAbandonedBasket_items0_product_images(): IImages {
  const abandonedBasket_items0_product_images: IImages = {
    thumbnail: 'abandonedBasket.items.0.product.images.thumbnail',
    additional: { yourKey: 'abandonedBasket.items.0.product.images.additional.yourKey' }, //map string , strings
  };
  return abandonedBasket_items0_product_images;
}

function getAbandonedBasket_items0_product_costs_discount(): IDiscount {
  const cost: IPrice = {
    currency: 'abandonedBasket.items.0.product.costs.discount.cost.currency',
    currencySymbol: 'abandonedBasket.items.0.product.costs.discount.cost.currencySymbol',
    decimal: 'abandonedBasket.items.0.product.costs.discount.cost.decimal',
    stripped: 'abandonedBasket.items.0.product.costs.discount.cost.stripped',
    text: 'abandonedBasket.items.0.product.costs.discount.cost.text',
  };

  const discount: IDiscount = {
    cost: cost,
    label: 'abandonedBasket.items.0.product.costs.discount.cost.label',
    code: 'abandonedBasket.items.0.product.costs.discount.cost.code',
  };
  return discount;
}

function getAbandonedBasket_items0_product_costs_shipping(): IShipping {
  const address: IAddress = {
    line1: 'abandonedBasket.items.0.product.costs.shipping.address.line1',
    line2: 'abandonedBasket.items.0.product.costs.shipping.address.line2',
    city: 'abandonedBasket.items.0.product.costs.shipping.address.city',
    country: 'abandonedBasket.items.0.product.costs.shipping.address.country',
    county: 'abandonedBasket.items.0.product.costs.shipping.address.county',
    postcode: 'abandonedBasket.items.0.product.costs.shipping.address.postcode',
    freeText: 'abandonedBasket.items.0.product.costs.shipping.address.freeText',
  };

  const cost: IPrice = {
    currency: 'abandonedBasket.items.0.product.costs.shipping.cost.currency',
    currencySymbol: 'abandonedBasket.items.0.product.costs.shipping.cost.currencySymbol',
    decimal: 'abandonedBasket.items.0.product.costs.shipping.cost.decimal',
    stripped: 'abandonedBasket.items.0.product.costs.shipping.cost.stripped',
    text: 'abandonedBasket.items.0.product.costs.shipping.cost.text',
  };
  const shipping: IShipping = {
    address: address,
    cost: cost,
    label: 'abandonedBasket.items.0.product.costs.shipping.label',
    value: 'abandonedBasket.items.0.product.costs.shipping.value',
  };
  return shipping;
}

function getAbandonedBasket_items0_product_costs_contract(): IContract {
  const monthlyCost: IPrice = {
    currency: 'abandonedBasket.items.0.product.costs.contract.monthlyCost.currency',
    currencySymbol: 'abandonedBasket.items.0.product.costs.contract.monthlyCost.currencySymbol',
    decimal: 'abandonedBasket.items.0.product.costs.contract.monthlyCost.cost.decimal',
    stripped: 'abandonedBasket.items.0.product.costs.contract.monthlyCost.cost.stripped',
    text: 'abandonedBasket.items.0.product.costs.contract.monthlyCost.cost.text',
  };
  const oneOffCost: IPrice = {
    currency: 'abandonedBasket.items.0.product.costs.contract.oneOffCost.currency',
    currencySymbol: 'abandonedBasket.items.0.product.costs.contract.oneOffCost.currencySymbol',
    decimal: 'abandonedBasket.items.0.product.costs.contract.oneOffCost.cost.decimal',
    stripped: 'abandonedBasket.items.0.product.costs.contract.oneOffCost.cost.stripped',
    text: 'abandonedBasket.items.0.product.costs.contract.oneOffCost.cost.text',
  };

  const totalContractPrice: IPrice = {
    currency: 'abandonedBasket.items.0.product.costs.contract.totalContractPrice.currency',
    currencySymbol: 'abandonedBasket.items.0.product.costs.contract.totalContractPrice.currencySymbol',
    decimal: 'abandonedBasket.items.0.product.costs.contract.totalContractPrice.cost.decimal',
    stripped: 'abandonedBasket.items.0.product.costs.contract.totalContractPrice.cost.stripped',
    text: 'abandonedBasket.items.0.product.costs.contract.totalContractPrice.cost.text',
  };

  const contract: IContract = {
    monthlyCost: monthlyCost,
    contractLength: 'abandonedBasket.items.0.product.costs.contractLength',
    oneOffCost: oneOffCost,
    totalContractPrice: totalContractPrice,
  };
  return contract;
}

function getAbandonedBasket_items0_product_costs(): IProductCosts {
  const abandonedBasket_items0_product_costs: IProductCosts = {
    singleItem: getAbandonedBasket_items0_product_costs_singleItem(),
    previous: getAbandonedBasket_items0_product_costs_previous(),
    taxes: getAbandonedBasket_items0_product_costs_taxes(),
    discount: getAbandonedBasket_items0_product_costs_discount(),
    shipping: getAbandonedBasket_items0_product_costs_shipping(),
    contract: getAbandonedBasket_items0_product_costs_contract(),
  };
  return abandonedBasket_items0_product_costs;
}

function getAbandonedBasket_items0_product_color(): IProductColor {
  const abandonedBasket_items0_product_color: IProductColor = {
    label: 'abandonedBasket.items.0.product.color.label',
    value: 'abandonedBasket.items.0.product.color.value',
  };
  return abandonedBasket_items0_product_color;
}

function getAbandonedBasket_items(): IBasketItem[] {
  const abandonedBasket_items: IBasketItem[] = [
    {
      product: getAbandonedBasket_items0_product(),
      costs: getAbandonedBasket_items0_costs(),
      quantity: 'abandonedBasket.items.0.quantity',
      subItems: undefined, //IBasketItem[] -recursive so not defined,
      clientRecommendations: getAbandonedBasket_items0_ClientRecommendations(),
    },
  ];
  return abandonedBasket_items;
}

function getAbandonedBasket_shipping(): IShipping {
  const address: IAddress = {
    line1: 'abandonedBasket.shipping.address.line1',
    line2: 'abandonedBasket.shipping.address.line2',
    city: 'abandonedBasket.shipping.address.city',
    country: 'abandonedBasket.shipping.address.country',
    county: 'abandonedBasket.shipping.address.county',
    postcode: 'abandonedBasket.shipping.address.postcode',
    freeText: 'abandonedBasket.shipping.address.freeText',
  };

  const cost: IPrice = {
    currency: 'abandonedBasket.shipping.cost.currency',
    currencySymbol: 'abandonedBasket.shipping.cost.currencySymbol',
    decimal: 'abandonedBasket.shipping.cost.decimal',
    stripped: 'abandonedBasket.shipping.cost.stripped',
    text: 'abandonedBasket.shipping.cost.text',
  };
  const shipping: IShipping = {
    address: address,
    cost: cost,
    label: 'abandonedBasket.shipping.label',
    value: 'abandonedBasket.shipping.value',
  };
  return shipping;
}
function getAbandonedBasket(): IBasket {
  const abandonedBasket: IBasket = {
    costs: getAbandonedBasket_costs(),
    currency: 'abandonedBasket.currency',
    currencySymbol: 'abandonedBasket.currencySymbol',
    discount: getAbandonedBasket_discount(),
    items: getAbandonedBasket_items(),
    repopulationUrl: 'abandonedBasket.repopulationurl',
    shipping: getAbandonedBasket_shipping(),
    paymentType: 'abandonedBasket.paymentType',
    type: 'abandonedBasket.type',
    sendOverride: 'abandonedBasket.sendOverride',
  };
  return abandonedBasket;
}

function getAbandonedBasket_items0_product_size(): IProductSize {
  const abandonedBasket_items0_product_size: IProductSize = {
    label: 'abandonedBasket.items.0.product.size.label',
    value: 'abandonedBasket.items.0.product.size.value',
  };
  return abandonedBasket_items0_product_size;
}

function getAbandonedBasket_items0_product_category(): IProductCategory {
  const abandonedBasket_items0_product_category: IProductCategory = {
    id: 'abandonedBasket.items.0.product.category.id',
    hierarchy: ['abandonedBasket.items.0.product.category.hierarchy.0'],
    label: 'abandonedBasket.items.0.product.category.label',
    value: 'abandonedBasket.items.0.product.category.value',
  };
  return abandonedBasket_items0_product_category;
}

function getAbandonedBasket_items0_product_accommodation(): ITravelAccommodation {
  const start: ITravelAccommodationDate = {
    date: 'abandonedBasket.items.0.product.accommodation.start.date',
    formattedDate: 'abandonedBasket.items.0.product.accommodation.start.formattedDate',
    time: 'abandonedBasket.items.0.product.accommodation.start.time',
    locale: 'abandonedBasket.items.0.product.accommodation.start.locale',
    dateFormat: 'abandonedBasket.items.0.product.accommodation.start.dateFormat',
  };

  const end: ITravelAccommodationDate = {
    date: 'abandonedBasket.items.0.product.accommodation.end.date',
    formattedDate: 'abandonedBasket.items.0.product.accommodation.end.formattedDate',
    time: 'abandonedBasket.items.0.product.accommodation.end.time',
    locale: 'abandonedBasket.items.0.product.accommodation.end.locale',
    dateFormat: 'abandonedBasket.items.0.product.accommodation.end.dateFormat',
  };

  const occupants: IPassengers = {
    adults: 'abandonedBasket.items.0.product.accommodation.occupants.adults',
    children: 'abandonedBasket.items.0.product.accommodation.occupants.children',
    infants: 'abandonedBasket.items.0.product.accommodation.occupants.infants',
  };

  const duration: IDuration = {
    nights: 'abandonedBasket.items.0.product.accommodation.duration.nights',
  };

  const abandonedBasket_items0_product_accommodation: ITravelAccommodation = {
    start: start,
    end: end,
    location: 'abandonedBasket.items.0.product.accommodation.location',
    name: 'abandonedBasket.items.0.product.accommodation.name',
    roomType: 'abandonedBasket.items.0.product.accommodation.roomType',
    numRooms: 'abandonedBasket.items.0.product.accommodation.numRooms',
    occupants: occupants,
    duration: duration,
    boardType: 'abandonedBasket.items.0.product.accommodation.boardType',
  };
  return abandonedBasket_items0_product_accommodation;
}

function getAbandonedBasket_items0_costs_subtotal(): IPrice {
  const abandonedBasket_items0_costs_subtotal: IPrice = {
    currency: 'abandonedBasket.items.0.costs.subtotal.currency',
    currencySymbol: 'abandonedBasket.items.0.costs.subtotal.currencySymbol',
    decimal: 'abandonedBasket.items.0.costs.subtotal.decimal',
    stripped: 'abandonedBasket.items.0.costs.subtotal.stripped',
    text: 'abandonedBasket.items.0.costs.subtotal.text',
  };
  return abandonedBasket_items0_costs_subtotal;
}

function getAbandonedBasket_items0_costs_total(): IPrice {
  const abandonedBasket_items0_costs_total: IPrice = {
    currency: 'abandonedBasket.items.0.costs.total.currency',
    currencySymbol: 'abandonedBasket.items.0.costs.total.currencySymbol',
    decimal: 'abandonedBasket.items.0.costs.total.decimal',
    stripped: 'abandonedBasket.items.0.costs.total.stripped',
    text: 'abandonedBasket.items.0.costs.total.text',
  };
  return abandonedBasket_items0_costs_total;
}
function getAbandonedBasket_items0_costs_taxes(): ITaxes {
  const abandonedBasket_items0_costs_taxes_sales_cost: IPrice = {
    currency: 'abandonedBasket.items.0.costs.taxes.sales.cost.currency',
    currencySymbol: 'abandonedBasket.items.0.costs.taxes.sales.cost.currencySymbol',
    decimal: 'abandonedBasket.items.0.costs.taxes.sales.cost.decimal',
    stripped: 'abandonedBasket.items.0.costs.taxes.sales.cost.stripped',
    text: 'abandonedBasket.items.0.costs.taxes.sales.cost.text',
  };

  const abandonedBasket_items0_costs_taxes_sales: ITax = {
    cost: abandonedBasket_items0_costs_taxes_sales_cost,
    label: 'abandonedBasket.items.0.costs.taxes.sales.label',
  };

  const abandonedBasket_items0_costs_taxes: ITaxes = {
    sales: abandonedBasket_items0_costs_taxes_sales,
  };
  return abandonedBasket_items0_costs_taxes;
}

function getAbandonedBasket_items0_product_rating(): IRating {
  const abandonedBasket_items0_product_rating: IRating = {
    label: 'abandonedBasket.items.0.product.rating.label',
    value: 'abandonedBasket.items.0.product.rating.value',
  };
  return abandonedBasket_items0_product_rating;
}

function getAbandonedBasket_items0_product_journey(): ITravelJourney[] {
  const passengers: IPassengers = {
    adults: 'abandonedBasket.items.0.product.journey.0.passengers.adults',
    children: 'abandonedBasket.items.0.product.journey.0.passengers.children',
    infants: 'abandonedBasket.items.0.product.journey.0.passengers.infants',
  };

  const grade: IGrade = {
    label: 'abandonedBasket.items.0.product.journey.0.grade.label',
    value: 'abandonedBasket.items.0.product.journey.0.grade.value',
  };

  const airline: IAirline = {
    name: 'abandonedBasket.items.0.product.journey.0.airline.name',
  };
  const abandonedBasket_items0_product_journey: ITravelJourney[] = [
    {
      start: getAbandonedBasket_items0_product_journey_start(),
      end: getAbandonedBasket_items0_product_journey_end(),
      direction: 'abandonedBasket.items.0.product.journey.0.direction',
      duration: 'abandonedBasket.items.0.product.journey.0.duration',
      flightName: 'abandonedBasket.items.0.product.journey.0.flightName',
      flightNumber: 'abandonedBasket.items.0.product.journey.0.flightNumber',
      passengers: passengers,
      type: 'abandonedBasket.items.0.product.journey.0.type',
      grade: grade,
      airline: airline,
    },
  ];
  return abandonedBasket_items0_product_journey;
}

function getAbandonedBasket_items0_product_journey_start(): ITravelWhereWhen {
  const startWhere: ITravelWhere = {
    iataCode: 'abandonedBasket.items.0.product.journey.0.start.where.iataCode',
    value: 'abandonedBasket.items.0.product.journey.0.start.where.value',
    label: 'abandonedBasket.items.0.product.journey.0.start.where.label',
  };
  const startWhen: ITravelWhen = {
    date: 'abandonedBasket.items.0.product.journey.0.start.when.date',
    formattedDate: 'abandonedBasket.items.0.product.journey.0.start.when.formattedDate',
    time: 'abandonedBasket.items.0.product.journey.0.start.when.time',
    locale: 'abandonedBasket.items.0.product.journey.0.start.when.locale',
    dateFormat: 'abandonedBasket.items.0.product.journey.0.start.when.dateFormat',
  };
  const start: ITravelWhereWhen = {
    where: startWhere,
    when: startWhen,
  };
  return start;
}

function getAbandonedBasket_items0_product_journey_end(): ITravelWhereWhen {
  const endWhere: ITravelWhere = {
    iataCode: 'abandonedBasket.items.0.product.journey.0.end.where.iataCode',
    value: 'abandonedBasket.items.0.product.journey.0.end.where.value',
    label: 'abandonedBasket.items.0.product.journey.0.end.where.label',
  };
  const endWhen: ITravelWhen = {
    date: 'abandonedBasket.items.0.product.journey.0.end.when.date',
    formattedDate: 'abandonedBasket.items.0.product.journey.0.end.when.formattedDate',
    time: 'abandonedBasket.items.0.product.journey.0.end.when.time',
    locale: 'abandonedBasket.items.0.product.journey.0.end.when.locale',
    dateFormat: 'abandonedBasket.items.0.product.journey.0.end.when.dateFormat',
  };
  const end: ITravelWhereWhen = {
    where: endWhere,
    when: endWhen,
  };
  return end;
}

function getAbandonedBasket_items0_product_ticket(): IProductTicket {
  const ticket: IProductTicket = {
    accessName: 'abandonedBasket.items.0.product.ticket.accessName',
    accessValue: 'abandonedBasket.items.0.product.ticket.accessValue',
    eventAddress: 'abandonedBasket.items.0.product.ticket.eventAddress',
    date: 'abandonedBasket.items.0.product.ticket.date',
    seatName: 'abandonedBasket.items.0.product.ticket.seatName',
    seatValue: 'abandonedBasket.items.0.product.ticket.seatValue',
    sectionName: 'abandonedBasket.items.0.product.ticket.sectionName',
    sectionValue: 'abandonedBasket.items.0.product.ticket.sectionValue',
  };
  return ticket;
}

function getAbandonedBasket_items0_product(): IProduct {
  const abandonedBasket_items0_product: IProduct = {
    name: 'abandonedBasket.items.0.product.name',
    url: 'abandonedBasket.items.0.product.url',
    images: getAbandonedBasket_items0_product_images(),
    description: 'abandonedBasket.items.0.product.description',
    currency: 'abandonedBasket.items.0.product.currency',
    currencySymbol: 'abandonedBasket.items.0.product.currencySymbol',
    costs: getAbandonedBasket_items0_product_costs(),
    color: getAbandonedBasket_items0_product_color(),
    size: getAbandonedBasket_items0_product_size(),
    inStock: 'abandonedBasket.items.0.product.inStock',
    category: getAbandonedBasket_items0_product_category(),
    accommodation: getAbandonedBasket_items0_product_accommodation(),
    journey: getAbandonedBasket_items0_product_journey(),
    brand: 'abandonedBasket.items.0.product.brand',
    metricId: 'abandonedBasket.items.0.product.metricId',
    metricValue: 'abandonedBasket.items.0.product.metricValue',
    clientProductId: 'abandonedBasket.items.0.product.clientProductId',
    rating: getAbandonedBasket_items0_product_rating(),
    ticket: getAbandonedBasket_items0_product_ticket(),
    isLowStock: 'abandonedBasket.items.0.product.isLowStock',
  };
  return abandonedBasket_items0_product;
}

function getAbandonedBasket_items0_costs(): IBasketCosts {
  const abandonedBasket_items0_costs_claim: IPrice = {
    currency: 'abandonedBasket.items.0.costs.claim.currency',
    currencySymbol: 'abandonedBasket.items.0.costs.claim.currencySymbol',
    decimal: 'abandonedBasket.items.0.costs.claim.decimal',
    stripped: 'abandonedBasket.items.0.costs.claim.stripped',
    text: 'abandonedBasket.items.0.costs.claim.text',
  };

  const abandonedBasket_items0_costs: IBasketCosts = {
    subtotal: getAbandonedBasket_items0_costs_subtotal(),
    total: getAbandonedBasket_items0_costs_total(),
    taxes: getAbandonedBasket_items0_costs_taxes(),
    claim: abandonedBasket_items0_costs_claim,
  };
  return abandonedBasket_items0_costs;
}

function getBrowsedProducts(): IProduct {
  const browsedProducts: IProduct = {
    name: 'browsedProducts.0.product.name',
    id: 'browsedProducts.0.product.id,',
    url: 'browsedProducts.0.product.url',
    images: getbrowsedProducts_0_product_images(),
    description: 'browsedProducts.0.product.description',
    currency: 'browsedProducts.0.product.currency',
    currencySymbol: 'browsedProducts.0.product.currencySymbol',
    costs: getbrowsedProducts_0_product_costs(),
    color: getbrowsedProducts_0_product_color(),
    size: getbrowsedProducts_0_product_size(),
    inStock: 'browsedProducts.0.product.inStock',
    category: getbrowsedProducts_0_product_category(),
    accommodation: getbrowsedProducts_0_product_accommodation(),
    journey: getbrowsedProducts_0_product_journey(),
    brand: 'browsedProducts.0.product.brand',
    metricId: 'browsedProducts.0.product.metricId',
    metricValue: 'browsedProducts.0.product.metricValue',
    clientProductId: 'browsedProducts.0.product.clientProductId',
    rating: getbrowsedProducts_0_product_rating(),
    ticket: getbrowsedProducts_0_product_ticket(),
  };
  return browsedProducts;
}

function getbrowsedProducts_0_product_images(): IImages {
  const browsedProducts_0_product_images: IImages = {
    thumbnail: 'browsedProducts.0.product.images.thumbnail',
    additional: { yourKey: 'browsedProducts.0.product.images.additional.yourKey' }, //map string , strings
  };
  return browsedProducts_0_product_images;
}

function getbrowsedProducts_0_product_costs(): IProductCosts {
  const browsedProducts_0_product_costs: IProductCosts = {
    singleItem: getbrowsedProducts_0_product_costs_singleItem(),
    previous: getbrowsedProducts_0_product_costs_previous(),
    taxes: getbrowsedProducts_0_product_costs_taxes(),
    discount: getbrowsedProducts_0_product_costs_discount(),
    shipping: getbrowsedProducts_0_product_costs_shipping(),
    contract: getbrowsedProducts_0_product_costs_contract(),
  };
  return browsedProducts_0_product_costs;
}

function getbrowsedProducts_0_product_costs_singleItem(): IPrice {
  const browsedProducts_0_product_costs_singleItem: IPrice = {
    currency: 'browsedProducts.0.product.costs.singleItem.currency',
    currencySymbol: 'browsedProducts.0.product.costs.singleItem.currencySymbol',
    decimal: 'browsedProducts.0.product.costs.singleItem.decimal',
    stripped: 'browsedProducts.0.product.costs.singleItem.stripped',
    text: 'browsedProducts.0.product.costs.singleItem.text',
  };
  return browsedProducts_0_product_costs_singleItem;
}

function getbrowsedProducts_0_product_costs_previous(): IPrice {
  const browsedProducts_0_product_costs_previous: IPrice = {
    currency: 'browsedProducts.0.product.costs.previous.currency',
    currencySymbol: 'browsedProducts.0.product.costs.previous.currencySymbol',
    decimal: 'browsedProducts.0.product.costs.previous.decimal',
    stripped: 'browsedProducts.0.product.costs.previous.stripped',
    text: 'browsedProducts.0.product.costs.previous.text',
  };
  return browsedProducts_0_product_costs_previous;
}

function getbrowsedProducts_0_product_costs_taxes(): ITaxes {
  const browsedProducts_0_product_costs_taxes_sales_cost: IPrice = {
    currency: 'browsedProducts.0.product.costs.taxes.sales.cost.currency',
    currencySymbol: 'browsedProducts.0.product.costs.taxes.sales.cost.currencySymbol',
    decimal: 'browsedProducts.0.product.costs.taxes.sales.cost.decimal',
    stripped: 'browsedProducts.0.product.costs.taxes.sales.cost.stripped',
    text: 'browsedProducts..0.product.costs.taxes.sales.cost.text',
  };

  const browsedProducts_0_product_costs_taxes_sales: ITax = {
    cost: browsedProducts_0_product_costs_taxes_sales_cost,
    label: 'browsedProducts.0.product.costs.taxes.sales.label',
  };

  const browsedProducts_0_product_costs_taxes: ITaxes = {
    sales: browsedProducts_0_product_costs_taxes_sales,
  };
  return browsedProducts_0_product_costs_taxes;
}

function getbrowsedProducts_0_product_costs_discount(): IDiscount {
  const cost: IPrice = {
    currency: 'browsedProducts.0.product.costs.discount.cost.currency',
    currencySymbol: 'browsedProducts.0.product.costs.discount.cost.currencySymbol',
    decimal: 'browsedProducts.0.product.costs.discount.cost.decimal',
    stripped: 'browsedProducts.0.product.costs.discount.cost.stripped',
    text: 'browsedProducts.0.product.costs.discount.cost.text',
  };

  const productDiscount: IDiscount = {
    cost: cost,
    label: 'browsedProducts.0.product.costs.discount.cost.label',
    code: 'browsedProducts..0.product.costs.discount.cost.code',
  };
  return productDiscount;
}

function getbrowsedProducts_0_product_costs_shipping(): IShipping {
  const address: IAddress = {
    line1: 'browsedProducts.0.product.costs.shipping.address.line1',
    line2: 'browsedProducts.0.product.costs.shipping.address.line2',
    city: 'browsedProducts.0.product.costs.shipping.address.city',
    country: 'browsedProducts.0.product.costs.shipping.address.country',
    county: 'browsedProducts.0.product.costs.shipping.address.county',
    postcode: 'browsedProducts.0.product.costs.shipping.address.postcode',
    freeText: 'browsedProducts.0.product.costs.shipping.address.freeText',
  };

  const cost: IPrice = {
    currency: 'browsedProducts.0.product.costs.shipping.cost.currency',
    currencySymbol: 'browsedProducts.0.product.costs.shipping.cost.currencySymbol',
    decimal: 'browsedProducts.0.product.costs.shipping.cost.decimal',
    stripped: 'browsedProducts.0.product.costs.shipping.cost.stripped',
    text: 'browsedProducts.0.product.costs.shipping.cost.text',
  };
  const shipping: IShipping = {
    address: address,
    cost: cost,
    label: 'browsedProducts.0.product.costs.shipping.label',
    value: 'browsedProducts.0.product.costs.shipping.value',
  };
  return shipping;
}

function getbrowsedProducts_0_product_costs_contract(): IContract {
  const monthlyCost: IPrice = {
    currency: 'browsedProducts.0.product.costs.contract.monthlyCost.currency',
    currencySymbol: 'browsedProducts.0.product.costs.contract.monthlyCost.currencySymbol',
    decimal: 'browsedProducts.0.product.costs.contract.monthlyCost.cost.decimal',
    stripped: 'browsedProducts.0.product.costs.contract.monthlyCost.cost.stripped',
    text: 'browsedProducts.0.product.costs.contract.monthlyCost.cost.text',
  };
  const oneOffCost: IPrice = {
    currency: 'browsedProducts.0.product.costs.contract.oneOffCost.currency',
    currencySymbol: 'browsedProducts.0.product.costs.contract.oneOffCost.currencySymbol',
    decimal: 'browsedProducts.0.product.costs.contract.oneOffCost.cost.decimal',
    stripped: 'browsedProducts.0.product.costs.contract.oneOffCost.cost.stripped',
    text: 'browsedProducts.0.product.costs.contract.oneOffCost.cost.text',
  };

  const totalContractPrice: IPrice = {
    currency: 'browsedProducts.0.product.costs.contract.totalContractPrice.currency',
    currencySymbol: 'browsedProducts.0.product.costs.contract.totalContractPrice.currencySymbol',
    decimal: 'browsedProducts.0.product.costs.contract.totalContractPrice.cost.decimal',
    stripped: 'browsedProducts.0.product.costs.contract.totalContractPrice.cost.stripped',
    text: 'browsedProducts.0.product.costs.contract.totalContractPrice.cost.text',
  };

  const contract: IContract = {
    monthlyCost: monthlyCost,
    contractLength: 'browsedProducts.0.product.costs.contractLength',
    oneOffCost: oneOffCost,
    totalContractPrice: totalContractPrice,
  };
  return contract;
}

function getbrowsedProducts_0_product_color(): IProductColor {
  const browsedProducts_0_product_color: IProductColor = {
    label: 'browsedProducts.0.product.color.label',
    value: 'browsedProducts.0.product.color.value',
  };
  return browsedProducts_0_product_color;
}

function getbrowsedProducts_0_product_size(): IProductSize {
  const browsedProducts_0_product_size: IProductSize = {
    label: 'browsedProducts.0.product.size.label',
    value: 'browsedProducts.items.0.product.size.value',
  };
  return browsedProducts_0_product_size;
}

function getbrowsedProducts_0_product_category(): IProductCategory {
  const browsedProducts_0_product_category: IProductCategory = {
    id: 'browsedProducts.0.product.category.id',
    hierarchy: ['browsedProducts.0.product.category.hierarchy.0'],
    label: 'browsedProducts.0.product.category.label',
    value: 'browsedProducts.0.product.category.value',
  };
  return browsedProducts_0_product_category;
}

function getbrowsedProducts_0_product_accommodation(): ITravelAccommodation {
  const start: ITravelAccommodationDate = {
    date: 'browsedProducts.0.product.accommodation.start.date',
    formattedDate: 'browsedProducts.0.product.accommodation.start.formattedDate',
    time: 'browsedProducts.0.product.accommodation.start.time',
    locale: 'browsedProducts.0.product.accommodation.start.locale',
    dateFormat: 'browsedProducts.0.product.accommodation.start.dateFormat',
  };

  const end: ITravelAccommodationDate = {
    date: 'browsedProducts.0.product.accommodation.end.date',
    formattedDate: 'browsedProducts.0.product.accommodation.end.formattedDate',
    time: 'browsedProducts.0.product.accommodation.end.time',
    locale: 'browsedProducts.0.product.accommodation.end.locale',
    dateFormat: 'browsedProducts.0.product.accommodation.end.dateFormat',
  };

  const occupants: IPassengers = {
    adults: 'browsedProducts.0.product.accommodation.occupants.adults',
    children: 'browsedProducts.0.product.accommodation.occupants.children',
    infants: 'browsedProducts.0.product.accommodation.occupants.infants',
  };

  const duration: IDuration = {
    nights: 'browsedProducts.0.product.accommodation.duration.nights',
  };

  const browsedProducts_0_product_accommodation: ITravelAccommodation = {
    start: start,
    end: end,
    location: 'browsedProducts.0.product.accommodation.location',
    name: 'browsedProducts.0.product.accommodation.name',
    roomType: 'browsedProducts.0.product.accommodation.roomType',
    numRooms: 'browsedProducts.0.product.accommodation.numRooms',
    occupants: occupants,
    duration: duration,
    boardType: 'browsedProducts.0.product.accommodation.boardType',
  };
  return browsedProducts_0_product_accommodation;
}

function getbrowsedProducts_0_product_rating(): IRating {
  const browsedProducts_0_product_rating: IRating = {
    label: 'browsedProducts.0.product.rating.label',
    value: 'browsedProducts.0.product.rating.value',
  };
  return browsedProducts_0_product_rating;
}

function getbrowsedProducts_0_product_journey(): ITravelJourney[] {
  const passengers: IPassengers = {
    adults: 'browsedProducts.0.product.journey.0.passengers.adults',
    children: 'browsedProducts.0.product.journey.0.passengers.children',
    infants: 'browsedProducts.0.product.journey.0.passengers.infants',
  };

  const grade: IGrade = {
    label: 'browsedProducts.0.product.journey.0.grade.label',
    value: 'browsedProducts.0.product.journey.0.grade.value',
  };

  const airline: IAirline = {
    name: 'browsedProducts.0.product.journey.0.airline.name',
  };
  const browsedProducts_0_product_journey: ITravelJourney[] = [
    {
      start: getbrowsedProducts_0_product_journey_start(),
      end: getbrowsedProducts_0_product_journey_end(),
      direction: 'browsedProducts.0.product.journey.0.direction',
      duration: 'browsedProducts.0.product.journey.0.duration',
      flightName: 'browsedProducts.0.product.journey.0.flightName',
      flightNumber: 'browsedProducts.0.product.journey.0.flightNumber',
      passengers: passengers,
      type: 'browsedProducts.0.product.journey.0.type',
      grade: grade,
      airline: airline,
    },
  ];
  return browsedProducts_0_product_journey;
}

function getbrowsedProducts_0_product_journey_start(): ITravelWhereWhen {
  const startWhere: ITravelWhere = {
    iataCode: 'browsedProducts.0.product.journey.0.start.where.iataCode',
    value: 'browsedProducts.product.journey.0.start.where.value',
    label: 'browsedProducts.0.product.journey.0.start.where.label',
  };
  const startWhen: ITravelWhen = {
    date: 'browsedProducts.0.product.journey.0.start.when.date',
    formattedDate: 'browsedProducts.0.product.journey.0.start.when.formattedDate',
    time: 'browsedProducts.0.product.journey.0.start.when.time',
    locale: 'browsedProducts.0.product.journey.0.start.when.locale',
    dateFormat: 'browsedProducts.0.product.journey.0.start.when.dateFormat',
  };
  const start: ITravelWhereWhen = {
    where: startWhere,
    when: startWhen,
  };
  return start;
}

function getbrowsedProducts_0_product_journey_end(): ITravelWhereWhen {
  const endWhere: ITravelWhere = {
    iataCode: 'browsedProducts.0.product.journey.0.end.where.iataCode',
    value: 'browsedProducts.0.product.journey.0.end.where.value',
    label: 'browsedProducts.0.product.journey.0.end.where.label',
  };
  const endWhen: ITravelWhen = {
    date: 'browsedProducts.0.product.journey.0.end.when.date',
    formattedDate: 'browsedProducts.0.product.journey.0.end.when.formattedDate',
    time: 'browsedProducts.0.product.journey.0.end.when.time',
    locale: 'browsedProducts.0.product.journey.0.end.when.locale',
    dateFormat: 'browsedProducts.0.product.journey.0.end.when.dateFormat',
  };
  const end: ITravelWhereWhen = {
    where: endWhere,
    when: endWhen,
  };
  return end;
}

function getbrowsedProducts_0_product_ticket(): IProductTicket {
  const ticket: IProductTicket = {
    accessName: 'browsedProducts.0.product.ticket.accessName',
    accessValue: 'browsedProducts.0.product.ticket.accessValue',
    eventAddress: 'browsedProducts.0.product.ticket.eventAddress',
    date: 'browsedProducts.0.product.ticket.date',
    seatName: 'browsedProducts.0.product.ticket.seatName',
    seatValue: 'browsedProducts.0.product.ticket.seatValue',
    sectionName: 'browsedProducts.0.product.ticket.sectionName',
    sectionValue: 'browsedProducts.0.product.ticket.sectionValue',
  };
  return ticket;
}

function getEmail(): IEmail {
  const email: IEmail = {
    value: 'email.value',
    source: 'email.source',
  };
  return email;
}
function getPhone(): IPhone {
  const phone: IPhone = {
    landline: 'phone.landline',
    mobile: 'phone.mobile',
    source: 'phone.source',
  };
  return phone;
}
function getGeoLocation(): IGeoLocation {
  const geoLocation: IGeoLocation = {
    continentCode: 'geoLocation.continentCode',
    countryCode: 'geoLocation.countryCode',
    countryName: 'geoLocation.countryName',
  };
  return geoLocation;
}

function getDevice(): IDevice {
  const device: IDevice = {
    device: getDeviceDevice(),
  };
  return device;
}

function getDeviceDevice(): IDeviceDevice {
  const deviceDevice: IDeviceDevice = {
    type: 'device.device.type',
  };
  return deviceDevice;
}

function getLocale(): ILocale {
  const locale: ILocale = {
    code: 'locale.code',
    language: 'locale.language',
    region: 'locale.region',
  };
  return locale;
}

function getCustomer(): ICustomer {
  const optIns: IOptIns = {
    email: 'customer.optIns.email',
    hasEmail: 'customer.optIns.hasEmail',
  };

  const address: IAddress = {
    line1: 'customer.address.line1',
    line2: 'customer.address.line2',
    city: 'customer.address.city',
    country: 'customer.address.country',
    county: 'customer.address.county',
    postcode: 'customer.address.postcode',
    freeText: 'customer.address.freeText',
  };

  const customer: ICustomer = {
    firstName: 'customer.firstName',
    lastName: 'customer.lastName',
    salutation: 'customer.salutation',
    optIns: optIns,
    id: 'customer.id',
    loggedIn: 'customer.loggedIn',
    address: address,
    dob: 'customer.dob',
    hasLoggedIn: 'customer.hasLoggedIn',
    type: 'customer.type',
    marketingStatus: 'customer.marketingStatus',
  };
  return customer;
}

function getRecommendations(): IRecommendations[] {
  const recommendations: IRecommendations[] = [
    {
      name: 'purchasedRecommendations.0.name',
      score: 'purchasedRecommendations.0.score',
      id: 'purchasedRecommendations.0.id',
      link: 'purchasedRecommendations.0.link',
      image: 'purchasedRecommendations.0.image',
      currencySymbol: 'purchasedRecommendations.0.currencySymbol',
      value: 'purchasedRecommendations.0.value',
    },
  ];
  return recommendations;
}

function getAbandonedBasket_items0_ClientRecommendations(): IClientRecommendation[] {
  const clientRecommendations: IClientRecommendation[] = [
    {
      name: 'abandonedBasket.items.0.clientRecommendations.0.name',
      url: 'abandonedBasket.items.0.clientRecommendations.0.url',
      image: 'abandonedBasket.items.0.clientRecommendations.0.image',
      cost: 'abandonedBasket.items.0.clientRecommendations.0.cost',
      description: 'abandonedBasket.items.0.clientRecommendations.0.description',
      brand: 'abandonedBasket.items.0.clientRecommendations.0.brand',
      id: 'abandonedBasket.items.0.clientRecommendations.0.id',
      label: 'abandonedBasket.items.0.clientRecommendations.0.label',
      prevCost: 'abandonedBasket.items.0.clientRecommendations.0.prevCost',
      saving: 'abandonedBasket.items.0.clientRecommendations.0.saving',
      size: 'abandonedBasket.items.0.clientRecommendations.0.size',
    },
  ];
  return clientRecommendations;
}
